import { useEffect, useState, useCallback } from 'react';

// `pipeline` is the NATIVE board (what used to be `pipeline-v2`) — promoted to the
// default tab 2026-08-11, so it serves `/`. The Affinity-fed board it replaced lived
// at `/pipeline-v1` until 2026-09-01, when the post-Affinity retirement wave deleted
// it along with `metrics`, `pipeline-by-sector` and `agenda` (owner rulings, plan
// docs/plans/strategic-architecture/post-affinity-stats-revival-and-simplification-plan.md).
// All four are in LEGACY_TAB_REDIRECTS below, so their bookmarks still land somewhere
// real. A tab key IS its URL slug here (`pathFromTab` / `tabFromUrl` both rely on it),
// which is why removing a key is also a `tab_visibility` data change (`s1tabretire1`).
export const TAB_KEYS = [
  'overview',
  'pipeline',
  'staffing',
  'intel',
  'sector-coverage',
  'deals',
  'prompts',
  'pipeline-insights',
  'outreach',
  'vocabularies',
  'all-deals',
  'crm',
  'nda',
  'settings',
  'admin',
] as const;

export type TabKey = (typeof TAB_KEYS)[number];

export const DEFAULT_TAB: TabKey = 'pipeline';

/**
 * Per-tab query-param ownership. Informational + documents the canonical
 * navigation contract (see docs/specs/architecture/navigation-deeplink-spec.md
 * §3.4). Plain TabBar navigation drops ALL query params (clean slate) so one
 * tab's selection/view state can never leak into another — this registry just
 * records which params each tab is allowed to own. Cross-tab deep links that
 * intentionally carry context pass them explicitly via `navigate(tab, {params})`.
 */
export const TAB_PARAMS: Partial<Record<TabKey, readonly string[]>> = {
  deals: ['deal_key', 'key', 'intent', 'return_to', 'status', 'scope', 'q', 'group', 'sort', 'users'],
  'pipeline-insights': ['mode', 'signal', 'deal_kind', 'deal_key', 'intake', 'cohort', 'field_name', 'days', 'pipeline', 'unresolved', 'q'],
  outreach: ['section', 'campaign', 'ctab', 'recipient', 'cfg', 'sender', 'q', 'status'],
  nda: ['review'],
  admin: ['group'],
  // CRM prove-out: q = debounced search text (replace-mode); sel = the ONE
  // selection param ('person:7' / 'org:9' / 'msg:5') — profile-to-profile opens
  // push a fresh entry with the new value, so Back walks the chain. rec = the
  // record-panel selection (a crm_list_entry id) opened OVER a list grid; rtab =
  // the panel's sub-view (replace-mode, per record-panel spec §7). Both drop on
  // any tab switch (§3.4), and rec/rtab are cleaned when `sel` moves off the
  // owning list (record-panel spec §7 cleanup rules). note = the note-overlay
  // selection (a crm_note id or 'new'), opened OVER a profile/record (Item E
  // §7.6, push-mode; dropped on tab switch and cleared when its host closes).
  crm: ['q', 'sel', 'rec', 'rtab', 'note'],
  // All Deals: the CRM surface reached through a shortcut tab that seeds `sel=list:<deal pipeline>`
  // on arrival, so it owns exactly the CRM params. The seed happens IN the page (AllDealsPage), not
  // on the TabBar click — a plain tab navigation drops every param by design (§3.4), so a param
  // smuggled through the click would be erased before the page mounted.
  'all-deals': ['q', 'sel', 'rec', 'rtab', 'note'],
};

// PR 7 — Legacy review tabs (trace + engine + signals) were retired in
// favor of Pipeline Insights. Old bookmarks and email links survive via
// a redirect map applied in `tabFromUrl` AND `normalizeLegacyTabQuery`.
// Each redirect can also tack on an extra query string so e.g. the
// Engine tab lands on Health mode rather than the Investigate default.
//
// PR2 — The legacy Suggestions inbox now redirects to Deal Updates, the
// operator successor surface. Preserve inert query params such as
// suggestionId for debugging, but do not render the retired page.
//
// 2026-08-11 — the v1/v2 swap. `pipeline-v2` was the native board's key for the whole
// fork window, and it is baked into links that are already delivered: every "Open
// Pipeline →" in the native completeness emails (services/dashboard_urls.py) plus any
// bookmark an operator saved during parallel-run. Those resolve to the promoted key,
// i.e. `/`. Note the DIRECTION: `/pipeline` and `?tab=pipeline` are NOT redirected —
// that key still exists and now means the native board, so an old Pipeline bookmark
// opens today's pipeline rather than the frozen one. Reaching v1 is explicit only.
//
// 2026-09-01 — the post-Affinity retirement wave. Metrics, Pipeline by Sector, Agenda and
// the Affinity-fed v1 board were RETIRED by owner ruling and their pages deleted. They get
// redirects rather than nothing for two reasons: `tabFromUrl` already falls back to
// DEFAULT_TAB for an unknown segment, so an entry here changes no render decision — but it
// also NORMALIZES the URL (`/metrics` → `/`), so a reload or a shared link stops claiming a
// tab that no longer exists. The target is the default native board: these four have no
// successor surface (unlike trace/engine/signals, which folded into Pipeline Insights), and
// the landing page is the one tab every caller can see.
const LEGACY_TAB_REDIRECTS: Record<string, { tab: TabKey; extraQuery?: string }> = {
  suggestions: { tab: 'deals' },
  trace: { tab: 'pipeline-insights' },
  engine: { tab: 'pipeline-insights', extraQuery: 'mode=health' },
  signals: { tab: 'pipeline-insights' },
  'pipeline-v2': { tab: 'pipeline' },
  // Retired 2026-09-01 (post-Affinity S1) — deleted tabs, redirected to the default board.
  metrics: { tab: 'pipeline' },
  'pipeline-by-sector': { tab: 'pipeline' },
  agenda: { tab: 'pipeline' },
  'pipeline-v1': { tab: 'pipeline' },
};

export function isTabKey(s: string | null | undefined): s is TabKey {
  return !!s && (TAB_KEYS as readonly string[]).includes(s);
}

/**
 * The tab to actually render, given the tab the URL asks for and the caller's
 * `/api/tabs` list. Falls back to the caller's FIRST visible tab when the URL names
 * one they cannot see — otherwise the app renders its chrome over an empty body,
 * which is what a URL-typed (or newly-closed) tab used to produce.
 *
 * Pure and synchronous ON PURPOSE. Doing this in an effect leaves one painted frame
 * showing the wrong thing, and the fix the owner asked for is precisely that nothing
 * is shown before visibility is known.
 *
 * `visible` un-resolved (`undefined` — still loading, or `/api/tabs` errored) returns
 * the requested tab unchanged: the caller decides whether to wait, and the server
 * gates every page independently, so rendering optimistically after an error degrades
 * to "the tab bar may be too generous" rather than "the dashboard is unusable".
 */
export function resolveVisibleTab(
  requested: TabKey,
  visible: readonly string[] | undefined,
): TabKey {
  if (!visible || visible.includes(requested)) return requested;
  return visible.find(isTabKey) ?? requested;
}

/**
 * Resolve the active tab from the URL.
 *
 * Prefers the path segment (canonical form). Falls back to `?tab=...` for
 * back-compat with legacy email links that predate path-based routing — see
 * docs/archive/pre-cut-2026-08/deep-link-routing-fix-spec.md. Without the fallback, the completeness
 * recap emails sitting in users' inboxes drop them on the Pipeline tab
 * instead of opening their target suggestion.
 */
function tabFromUrl(): TabKey {
  const segment = window.location.pathname.replace(/^\/+/, '').split('/')[0];
  // PR 7 — Retired tabs redirect into Pipeline Insights.
  if (segment in LEGACY_TAB_REDIRECTS) return LEGACY_TAB_REDIRECTS[segment].tab;
  if (isTabKey(segment)) return segment;
  const queryTab = new URLSearchParams(window.location.search).get('tab');
  if (queryTab && queryTab in LEGACY_TAB_REDIRECTS) {
    return LEGACY_TAB_REDIRECTS[queryTab].tab;
  }
  if (isTabKey(queryTab)) return queryTab;
  return DEFAULT_TAB;
}

function pathFromTab(tab: TabKey): string {
  return tab === DEFAULT_TAB ? '/' : `/${tab}`;
}

export interface NavigateOptions {
  /** Query params to carry to the destination tab (cross-tab deep links). Omit → clean slate. */
  params?: Record<string, string>;
  /** Hash fragment for the destination (e.g. '#field-name'). Leading '#' optional. */
  hash?: string;
  /** Use replaceState instead of pushState (no new Back entry). */
  replace?: boolean;
}

function buildTabUrl(tab: TabKey, opts: NavigateOptions = {}): string {
  const path = pathFromTab(tab);
  const sp = new URLSearchParams();
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v != null && v !== '') sp.set(k, v);
    }
  }
  const q = sp.toString();
  let h = opts.hash ?? '';
  if (h && !h.startsWith('#')) h = `#${h}`;
  return path + (q ? `?${q}` : '') + h;
}

/**
 * Imperative tab navigation usable outside the `useTabRouter` hook (e.g. from
 * `useReturnTo`, cross-tab deep links). Plain calls DROP all query params; pass
 * `opts.params` to carry context. Dispatches a `popstate` so the active
 * `useTabRouter` instance (and any `useUrlState` subscribers) re-resolve.
 */
export function navigateTab(tab: TabKey, opts: NavigateOptions = {}): void {
  const target = buildTabUrl(tab, opts);
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (target === current) return; // no-op: same destination, don't spam history
  window.history[opts.replace ? 'replaceState' : 'pushState'](null, '', target);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * If we arrived via a legacy `?tab=X` URL, rewrite the URL in-place to the
 * canonical path form. Preserves all other query params (notably
 * `suggestionId`) so deep-link behaviors downstream keep working.
 *
 * PR 7 — Also handles the retired-tab redirect: ?tab=trace → /pipeline-insights,
 * ?tab=engine → /pipeline-insights?mode=health, ?tab=signals → /pipeline-insights.
 * Path-form redirects (/trace, /engine, /signals) are handled separately so
 * legacy bookmarks survive.
 *
 * 2026-08-11 — same treatment for the v1/v2 swap: /pipeline-v2 and ?tab=pipeline-v2
 * normalize to `/`, the promoted native board.
 */
function normalizeLegacyTabQuery(): void {
  const url = new URL(window.location.href);
  // First handle path-form legacy tabs.
  const segment = url.pathname.replace(/^\/+/, '').split('/')[0];
  if (segment in LEGACY_TAB_REDIRECTS) {
    const r = LEGACY_TAB_REDIRECTS[segment];
    // pathFromTab, NOT `/${r.tab}` — a redirect whose target is the DEFAULT tab has to
    // land on `/`, and hardcoding the segment wrote `/pipeline` instead (harmless before
    // the v1/v2 swap, when no redirect pointed at the default tab; wrong the moment
    // `pipeline-v2` → `pipeline` was added).
    url.pathname = pathFromTab(r.tab);
    if (r.extraQuery) {
      const extra = new URLSearchParams(r.extraQuery);
      extra.forEach((v, k) => {
        if (!url.searchParams.has(k)) url.searchParams.set(k, v);
      });
    }
    window.history.replaceState(null, '', url.toString());
    return;
  }
  if (!url.searchParams.has('tab')) return;
  const queryTab = url.searchParams.get('tab');
  url.searchParams.delete('tab');
  if (queryTab && queryTab in LEGACY_TAB_REDIRECTS) {
    const r = LEGACY_TAB_REDIRECTS[queryTab];
    url.pathname = pathFromTab(r.tab); // see the path-form branch above

    if (r.extraQuery) {
      const extra = new URLSearchParams(r.extraQuery);
      extra.forEach((v, k) => {
        if (!url.searchParams.has(k)) url.searchParams.set(k, v);
      });
    }
  } else if (isTabKey(queryTab) && queryTab !== DEFAULT_TAB) {
    url.pathname = `/${queryTab}`;
  }
  window.history.replaceState(null, '', url.toString());
}

export function useTabRouter(): [TabKey, (tab: TabKey, opts?: NavigateOptions) => void] {
  const [activeTab, setActiveTab] = useState<TabKey>(() => tabFromUrl());

  useEffect(() => {
    // Normalize legacy `?tab=X` URLs once on mount so reloads + URL-sharing
    // use the canonical path form. Safe to run unconditionally; no-ops when
    // ?tab is absent.
    normalizeLegacyTabQuery();
    const onPopState = () => setActiveTab(tabFromUrl());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Plain TabBar navigation drops all query params (clean slate). Cross-tab
  // deep links pass `opts.params` explicitly. `navigateTab` dispatches popstate,
  // which the listener above turns into a setActiveTab — but we also set it
  // directly so the change is synchronous for the caller.
  const navigate = useCallback((tab: TabKey, opts?: NavigateOptions) => {
    navigateTab(tab, opts);
    setActiveTab(tab);
  }, []);

  return [activeTab, navigate];
}
